import { useCallback } from 'react'
import { useTheme } from 'styled-components'
import { useCreateTheme } from '@api/hooks/themes'
import { postProject } from '@api/Project'
import { addArea } from '../../../infra/http/service/api/Areas'
import { addSubject } from '../../../infra/http/service/api/Subjects'
import { addUnit } from '../../../infra/http/service/api/Units'
import { startProject } from '@api/Project'
import { startUnit, createUnitPreview, getUnitTasks } from '@api/Units'
import { toast } from 'react-toastify'
import { useAuth } from '@hooks/Authentication'
import { Project } from '../types/project'
import { Corporate } from '../types/corporate'
import { IProject } from '@domain/project'
import { format } from 'date-fns'
import { IAddAreaParams } from '@api/models/areas'
import { IAddSubject } from '@api/models/subjects'
import { IAddUnit } from '@api/models/units'
import { IUnitTask } from '@domain/units'
import { getRules } from '@api/Rules'
import { getCoverConfiguration } from '../utils/getCoverConfiguration'
import { convertH1ToDivTopic } from '../utils/convertH1ToDivTopic'
import getThemeConfiguration from '../utils/getThemeConfiguration'
import { awaitAutoFormatCorporateResources } from '../utils/awaitAutoFormatCorporateResources'

export const useCreateRealizeUnit = () => {
  const theme = useTheme()
  const { createTheme } = useCreateTheme()
  const { socket } = useAuth()

  const onCreateRealizeUnit = useCallback(
    async (
      project: Project,
      content: string,
      corporate: Corporate | null
    ): Promise<string | null> => {
      try {
        const assetsBaseUrl =
          typeof window !== 'undefined' ? window.location.origin : undefined
        const departments = corporate?.departments ?? []

        const [themeConfiguration, coverConfiguration] = await Promise.all([
          getThemeConfiguration({
            themeType: 'default',
            primaryColor: theme.colors.primary,
            secondaryColor: theme.colors.secondary,
            thirdColor: theme.colors.third,
            themeAssetsBaseUrl: assetsBaseUrl
          }),
          getCoverConfiguration({
            coverType: 'default',
            title: project.name,
            departments,
            coverAssetsBaseUrl: assetsBaseUrl
          })
        ])

        const themeResponse = await createTheme({
          name: project.name,
          thumbnail: '',
          config: themeConfiguration.config,
          resources: themeConfiguration.resources
        })

        const themeId = ((themeResponse?.data as unknown) as string) ?? ''

        const projectPayload: IProject & {
          themeId: string
          customizable: boolean
        } = {
          name: `${project.name} - Projeto`,
          procedureId: '',
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          themeId,
          customizable: true
        }

        const projectResponse = await postProject(projectPayload)

        const projectId = ((projectResponse?.data as unknown) as string) ?? ''

        const areaPayload: IAddAreaParams = {
          name: `${project.name} - Área`,
          projectId,
          color: '#000000'
        }

        const areaResponse = await addArea(areaPayload)

        const areaId = ((areaResponse?.data as unknown) as string) ?? ''

        const subjectPayload: IAddSubject = {
          name: `${project.name} - Disciplina`,
          areaId
        }

        const subjectResponse = await addSubject(subjectPayload)

        const subjectId = ((subjectResponse?.data as unknown) as string) ?? ''

        const unitPayload: IAddUnit = {
          name: project.name,
          subjectId,
          startDate: null,
          endDate: null
        }

        const unitResponse = await addUnit(unitPayload)

        const unitId = ((unitResponse?.data as unknown) as string) ?? ''

        await startProject(projectId)
        await startUnit(unitId)

        const [unitTasksResponse, rulesResponse] = await Promise.all([
          getUnitTasks(unitId, {
            allTasks: true
          }),
          getRules({
            id: unitId
          })
        ])

        const { tasks } = ((unitTasksResponse?.data as unknown) as {
          tasks: IUnitTask[]
        }) ?? { tasks: [] }

        const foundTask = tasks.find(function (item) {
          return item.isCurrent === true
        })

        const { template } = rulesResponse?.data

        const formattedContent = convertH1ToDivTopic(content)
        let finallyFormattedContent = formattedContent

        const corporateProjectId = corporate?.projectId
        if (
          socket &&
          corporateProjectId != null &&
          Number.isFinite(corporateProjectId) &&
          corporateProjectId > 0
        ) {
          const autoFormatted = await awaitAutoFormatCorporateResources({
            projectId: corporateProjectId,
            referenceUnitId: unitId,
            html: formattedContent,
            socket
          })
          if (autoFormatted) {
            finallyFormattedContent = autoFormatted
          }
        }

        const finalContent = JSON.stringify({
          cover: coverConfiguration,
          content: finallyFormattedContent
        })

        await createUnitPreview({
          unitId: unitId,
          taskId: foundTask?.id ?? '',
          html: finalContent,
          template: template.path
        })

        return unitId
      } catch (error) {
        if (error instanceof Error && 'response' in error) {
          const axiosError = error as {
            response?: { data: { reason: string } }
          }
          const reason = axiosError.response?.data.reason
          console.log(reason)
          if (reason === 'max-themes-quantity-reached') {
            toast.error('Você atingiu o limite de temas permitidos')
          } else {
            toast.error('Um erro ocorreu, tente criar novamente')
          }
        }
      }

      return null
    },
    [
      createTheme,
      socket,
      theme.colors.primary,
      theme.colors.secondary,
      theme.colors.third
    ]
  )

  return {
    onCreateRealizeUnit
  }
}
